// XLSX bulk-upload sub-flow for the stock-import "Add items" step.
//
// Two modes supported by the backend:
//   1. "single": file is a flat list of serials → must come with a chosen variant.
//   2. "multi":  file has variant_sku + serial_no columns → backend groups by sku.
//
// The UI picks mode based on whether the user has already selected a variant
// in the manual form. If yes we send variantId; if no we expect the file itself
// to carry the SKU column.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Download } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";

interface VariantContext {
  id: string;
  sku: string;
  productName: string;
}

interface Props {
  importId: string;
  /** Optional — when provided, mode="single" with this variant; else "multi". */
  variant?: VariantContext | null;
  /** Optional unit cost for single mode. */
  unitCost?: string;
  onDone?: () => void;
}

interface RowReport {
  row: number;
  status: "ok" | "skipped" | "failed";
  message?: string;
  key?: string;
}

interface BulkResult {
  mode: "single" | "multi";
  total: number;
  accepted: number;
  skipped: number;
  failed: number;
  itemsCreated: number;
  items?: Array<{ variantId: string; variantSku: string; serials: number }>;
  rows: RowReport[];
}

export function SerialXLSXUpload({ importId, variant, unitCost, onDone }: Props) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("no file");
      const fd = new FormData();
      fd.append("file", file);
      if (variant) fd.append("variantId", variant.id);
      if (variant && unitCost) fd.append("unitCost", unitCost);
      const res = await apiClient.post<{ data: BulkResult }>(
        `/v1/imports/${importId}/items/bulk-xlsx`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 10 * 60 * 1000 },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      onDone?.();
    },
  });

  const errorMsg = upload.error ? translateApiError(upload.error) : null;
  const result = upload.data;
  const failures = result?.rows.filter((r) => r.status !== "ok") ?? [];

  return (
    <div className="rounded-md border bg-muted/20 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <FileSpreadsheet size={14} /> Upload Excel
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {variant
              ? <>Chế độ <b>1 variant</b>: file chỉ cần 1 cột serial. Gán vào: <span className="font-mono">{variant.sku}</span></>
              : <>Chế độ <b>nhiều variant</b>: file cần 2 cột — <span className="font-mono">variant_sku</span> + <span className="font-mono">serial_no</span></>}
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadTemplate(!!variant)}
          className="text-xs text-primary hover:underline flex items-center gap-1 whitespace-nowrap"
        >
          <Download size={12} /> Tải template
        </button>
      </div>

      <input
        type="file"
        accept=".xlsx,.csv"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          upload.reset();
        }}
        className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
      />

      {file && !result && (
        <p className="text-xs text-muted-foreground">
          {file.name} · {(file.size / 1024).toFixed(1)} KB
        </p>
      )}

      {errorMsg && (
        <p className="rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive">{errorMsg}</p>
      )}

      {result && (
        <div className="rounded border bg-background p-3 text-xs space-y-1">
          <p>
            <b>{result.itemsCreated}</b> items · <b>{result.accepted}</b> serials thêm
            {result.skipped > 0 && <> · {result.skipped} bỏ qua</>}
            {result.failed > 0 && <> · <span className="text-destructive">{result.failed} lỗi</span></>}
          </p>
          {result.items && result.items.length > 0 && (
            <ul className="text-muted-foreground">
              {result.items.map((it) => (
                <li key={it.variantId}>· {it.variantSku}: {it.serials} serials</li>
              ))}
            </ul>
          )}
          {failures.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-amber-600 dark:text-amber-400">
                Xem {failures.length} dòng lỗi/bỏ qua
              </summary>
              <ul className="mt-1 max-h-36 overflow-y-auto font-mono text-[11px]">
                {failures.map((r, i) => (
                  <li key={i}>
                    dòng {r.row || "—"}: {r.status} {r.key && `[${r.key}]`} {r.message && `— ${r.message}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Button size="sm" onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
        {upload.isPending ? "Đang upload…" : "Upload"}
      </Button>
    </div>
  );
}

// Generates a minimal CSV template the user can save as xlsx. Keeping it
// client-side avoids shipping a binary asset and the BE accepts xlsx-saved
// CSVs equivalently — excelize will read either.
function downloadTemplate(single: boolean) {
  const csv = single
    ? "serial_no\n352099001761480\n352099001761481\n"
    : "variant_sku,serial_no,unit_cost\nSKU-IPH15-128,352099001761480,18000000\nSKU-IPH15-128,352099001761481,18000000\nSKU-IPH15-256,352099001761490,21000000\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = single ? "serial-template-single.csv" : "serial-template-multi.csv";
  a.click();
  URL.revokeObjectURL(url);
}
